import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  TextField,
  Autocomplete,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Checkbox,
  Button,
  tableCellClasses,
  TableContainer,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
  Select,
  MenuItem,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { LabelWithControl } from "components/common/LabelControl";
import { showNotificationDialog } from "redux/actions/notificationActions";
import { useDispatch, useSelector } from "react-redux";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  CUTOFF_ACTION,
  PRODUCT_DETAILS_TYPE,
  STORE,
  SUPPLYCHAIN,
} from "components/common/CONSTANTS";
import { getStoreCatalogueAction } from "redux/actions/createManualOrderStoresAction";
import { showErrorMessageByCode } from "services/commonServices";
import {
  createManualOrdersForSiteCanteen,
  resetCreateManualOrder,
  searchOrderOpportunities,
  searchOrderOpportunitiesReset,
} from "redux/actions/createAction";
import dayjs from "dayjs";
import { amendOrdersForSiteCanteen } from "redux/actions/processOrdersActions";
import { RESET_PROCESS_ORDERS } from "redux/actions/action-type";
import CutOffTimeAlert from "components/common/cutOffTimeAlert";
import { encryptString } from "components/common/EncryptDecrypt";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&:first-child": {
    padding: "0px",
    position: "sticky",
    left: 0,
    zIndex: 3,
    backgroundColor: theme.palette.background.paper,
  },
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.default,
    fontSize: 13,
    fontWeight: "bold",
    whiteSpace: "normal",
    position: "sticky",
    left: 0,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    position: "sticky",
    left: 0,
    backgroundColor: theme.palette.background.paper,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
}));

const StyledTableCellSticky = styled(TableCell)(
  ({ theme, stickyposition }) => ({
    [`&.${tableCellClasses.head}`]: {
      position: "sticky",
      left: stickyposition,
      backgroundColor: theme.palette.primary.main,
      zIndex: 999,
      color: theme.palette.background.default,
    },
    [`&.${tableCellClasses.body}`]: {
      position: "sticky",
      left: stickyposition,
      backgroundColor: theme.palette.background.paper,
      zIndex: 999,
      backgroundClip: "padding-box",
    },
  })
);

export const CreateManualOrderStores = () => {
  const [department, setDepartment] = useState("");
  const [category, setCategory] = useState("");
  const [store, setStore] = useState([]);
  const [mpin, setMpin] = useState("");
  const [description, setDescription] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [openDialogForCreate, setopenDialogForCreate] = useState(false);
  const dispatch = useDispatch();
  const [dateFilter, setDateFilter] = useState("All Days");
  const navigate = useNavigate();

  const { locationResponse } = useSelector((state) => state.locations);
  const userType = useSelector((state) => state.user);
  const { accessiblePages, userDetailResponse } = userType.userType;
  const [validStoreList, setValidStoreList] = useState([]);
  const [locationError, setLocationError] = useState("");
  const [minError, setMinError] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);

  const storeCatalogueReducer = useSelector(
    (state) => state.createManualOrderStores
  );
  const orderOpportunitiesReducer = useSelector((state) => state.orderOpp);
  const processOrdersReducer = useSelector((state) => state.processOrders);

  const [storeCatalogueList, setstoreCatalogueList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [ordersToUpdateList, setOrdersToUpdateList] = useState([]);
  const DEFAULT_MESSAGE = "Please enter your search criteria";
  const NO_DATA_FOUND_MESSAGE = "No records found";
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [enableCreateButton, setEnableCreateButton] = useState(false);

  const [totalOrdersCreated, setTotalOrdersCreated] = useState(0);
  const [totalOrdersUpdated, setTotalOrdersUpdated] = useState(0);
  const [totalOrdersCancelled, setTotalOrdersCancelled] = useState(0);
  const [totalBookmarksPosted, setTotalBookmarksPosted] = useState(0);
  const [totalOrdersBookmarked, setTotalOrdersBookmarked] = useState(0);
  const [totalCreateOrderFailed, setTotalCreateOrderFailed] = useState(0);
  const [totalUpdateOrderFailed, setTotalUpdateOrderFailed] = useState(0);
  const [totalCancelOrderFailed, setTotalCancelOrderFailed] = useState(0);
  const [totalBookmarkFailed, setTotalBookmarkFailed] = useState(0);
  const [offset, setOffset] = useState(0);
  const tableContainerRef = useRef();
  const [metadata, setMetadata] = useState({});
  const FETCH_ORDER_LIMIT = 10;
  const [updateDataOffset, setUpdateDataOffset] = useState(null);
  const [failedOrders, setFailedOrders] = useState([]);
  const [isDataLoadingByScroll, setDataLoadingByScroll] = useState(false);
  const [lastSixMondays, setLastSixMondays] = useState([]);
  const [displayCutOffTime, setDisplayCutOffTime] = useState(null);
  const { cutOffTimeResponse } = useSelector((state) => state.cutOffTime);
  const [isCutOffReachedForToday, setCutOffReachedForToday] = useState(false);
  const [activeRowIndex, setActiveRowIndex] = useState(null);

  const handleInfiniteScroll = useCallback(async () => {
    try {
      const { scrollTop, scrollHeight, clientHeight } =
        tableContainerRef.current;
      if (
        scrollTop + clientHeight >= scrollHeight - 1 &&
        !orderOpportunitiesReducer.isSearchOrderOpportunitiesLoading
      ) {
        if (metadata.next) {
          setShowSpinner(true);
        }
        setOffset(filteredData.length);
      }
    } catch (error) {
      console.log(error, "error");
    }
  }, [
    tableContainerRef,
    filteredData,
    orderOpportunitiesReducer.isSearchOrderOpportunitiesLoading,
    metadata.next,
  ]);

  useEffect(() => {
    if (filteredData.length > 0) {
      const tableContainer = tableContainerRef.current;
      tableContainer.addEventListener("scroll", handleInfiniteScroll);
      return () =>
        tableContainer.removeEventListener("scroll", handleInfiniteScroll);
    }
  }, [handleInfiniteScroll, filteredData]);

  const getDatesArray = (start, noOfDays) => {
    const week = [];
    for (let i = 0; i < noOfDays; i++) {
      const date = dayjs(start).add(i, "day");
      week.push(dayjs(date).format("YYYY-MM-DDT00:00:00"));
    }
    return week;
  };

  const getMondaysFromLastSixWeeks = () => {
    const mondays = [];

    for (let i = 1; i < 7; i++) {
      const monday = dayjs().subtract(i, "week").day(1); // Monday of that week
      mondays.push(monday.format("YYYY-MM-DD"));
    }

    setLastSixMondays(mondays);
  };

  const formatDateForLabelDisplay = (date) => {
    if (date) {
      return dayjs(date, "YYYY-MM-DDTHH:mm:ss").format("ddd DD/MM");
    } else {
      return "";
    }
  };

  const formatLastOrderDateForDisplay = (date) => {
    if (date) {
      return dayjs(date, "YYYY-MM-DDTHH:mm:ss").format("DD/MM/YYYY");
    } else {
      return "";
    }
  };

  useEffect(() => {
    if (accessiblePages) {
      const stores = [];
      if (userDetailResponse.colleagueStores.length > 0) {
        stores.push(...userDetailResponse.colleagueStores);
      } else {
        locationResponse.forEach(function (location) {
          if (
            location.channel.toLowerCase() === SUPPLYCHAIN &&
            location.locationType === STORE
          ) {
            stores.push(location.locationId);
          }
        });
      }
      stores.sort((a, b) => a - b);
      setValidStoreList(stores);
      getMondaysFromLastSixWeeks();
    }
  }, [accessiblePages, locationResponse, userDetailResponse]);

  useEffect(() => {
    if (validStoreList.length === 1) {
      setStore(validStoreList[0]);
    }
  }, [validStoreList]);

  useEffect(() => {
    dispatch(getStoreCatalogueAction(PRODUCT_DETAILS_TYPE.STORE_CATALOGUE));
  }, [dispatch]);

  useEffect(() => {
    if (storeCatalogueReducer.isGetStoreCatalogueLoading) {
      setShowSpinner(true);
    } else if (storeCatalogueReducer.isGetStoreCatalogueSuccess) {
      setstoreCatalogueList(storeCatalogueReducer.getStoreCatalogueResponse);
      const categories = [];
      storeCatalogueReducer.getStoreCatalogueResponse.forEach((item) => {
        if (!categories.includes(item.category)) {
          categories.push(item.category);
        }
      });
      categories.sort((a, b) => a.localeCompare(b));
      setAvailableCategories(categories);
      setShowSpinner(false);
    } else if (storeCatalogueReducer.isGetStoreCatalogueError) {
      setShowSpinner(false);
      if (
        storeCatalogueReducer.getStoreCatalogueResponse.response.status === 404
      ) {
        const errTitle = "404 - Data not found";
        const errContent =
          storeCatalogueReducer.getStoreCatalogueResponse.response.data
            .errorMessage;
        dispatch(showNotificationDialog(errTitle, errContent, "Ok"));
      } else {
        const { errTitle, errContent } = showErrorMessageByCode(
          storeCatalogueReducer.getStoreCatalogueResponse
        );
        dispatch(showNotificationDialog(errTitle, errContent, "Ok"));
        dispatch(searchOrderOpportunitiesReset());
      }
    }
  }, [storeCatalogueReducer, dispatch]);

  useEffect(() => {
    if (ordersToUpdateList.length > 0) {
      const validItems = ordersToUpdateList.filter(
        (item) =>
          item.isValidQuantity === true || item.isValidQuantity === undefined
      );
      setEnableCreateButton(validItems && validItems.length > 0);
    } else {
      setEnableCreateButton(false);
    }
  }, [ordersToUpdateList]);

  useEffect(() => {
    if (
      orderOpportunitiesReducer.isSearchOrderOpportunitiesLoading ||
      orderOpportunitiesReducer.isCreateManualOrderLoading
    ) {
      setShowSpinner(true);
    } else if (orderOpportunitiesReducer.isCreateManualOrderSuccess) {
      setShowSpinner(false);
      if (
        orderOpportunitiesReducer.createManualOrderResponse.orders &&
        orderOpportunitiesReducer.createManualOrderResponse.orders.length > 0
      ) {
        setFailedOrders([
          ...failedOrders,
          ...orderOpportunitiesReducer.createManualOrderResponse.orders,
        ]);
      }
      const metadata =
        orderOpportunitiesReducer.createManualOrderResponse.metadata;
      const totalPassed = metadata.totalrecordsProcessed + totalOrdersCreated;
      const totalFailed =
        totalUpdateOrderFailed +
        orderOpportunitiesReducer.createManualOrderResponse.orders
          ? orderOpportunitiesReducer.createManualOrderResponse.orders.length
          : 0;
      setTotalOrdersCreated(totalPassed);
      setTotalCreateOrderFailed(totalFailed);
      if (!openDialogForCreate) {
        setopenDialogForCreate(true);
      }
      dispatch(resetCreateManualOrder());
    } else if (orderOpportunitiesReducer.isCreateManualOrderError) {
      setShowSpinner(false);
      if (
        orderOpportunitiesReducer.createManualOrderResponse.orders &&
        orderOpportunitiesReducer.createManualOrderResponse.orders.length > 0
      ) {
        setFailedOrders([
          ...failedOrders,
          ...orderOpportunitiesReducer.createManualOrderResponse.orders,
        ]);
      }
      const metadata =
        orderOpportunitiesReducer.createManualOrderResponse.metadata;
      const totalPassed = metadata.totalrecordsProcessed + totalOrdersCreated;
      const totalFailed =
        totalUpdateOrderFailed +
        orderOpportunitiesReducer.createManualOrderResponse.orders
          ? orderOpportunitiesReducer.createManualOrderResponse.orders.length
          : 0;
      setTotalOrdersCreated(totalPassed);
      setTotalCreateOrderFailed(totalFailed);
      if (!openDialogForCreate) {
        setopenDialogForCreate(true);
      }
      dispatch(resetCreateManualOrder());
    } else if (orderOpportunitiesReducer.isSearchOrderOpportunitiesSuccess) {
      setMetadata(
        orderOpportunitiesReducer.searchOrderOpportunitiesResponse.metadata
      );
      if (
        orderOpportunitiesReducer.searchOrderOpportunitiesResponse &&
        orderOpportunitiesReducer.searchOrderOpportunitiesResponse.items
      ) {
        const newWeekDates = getDatesArray(
          dayjs(),
          dateFilter === "Current Day" ? 1 : 7
        );
        const orderOppurtunuties = JSON.parse(
          JSON.stringify(
            orderOpportunitiesReducer.searchOrderOpportunitiesResponse.items
          )
        );
        orderOppurtunuties.forEach((item) => {
          if (!item.isBookmark) {
            item.isBookmark = false;
          }
          item.newBookmarkValue = item.isBookmark;
          item.opportunityDates = [];
          newWeekDates.forEach((day) => {
            const opportunityDate = getOrderOpportunityDates(day, item.dates);
            if (opportunityDate) {
              const pendingOrder = ordersToUpdateList.filter(
                (orderToUpdate) =>
                  orderToUpdate.itemId === item.itemId &&
                  orderToUpdate.shipFromLocationId ===
                    item.shipFromLocationId &&
                  orderToUpdate.orderDate === opportunityDate.orderDate
              );

              if (pendingOrder && pendingOrder.length > 0) {
                opportunityDate.newQuantity = pendingOrder[0].quantityOrdered;
              }

              const failedOrder = failedOrders.filter(
                (orderToUpdate) =>
                  orderToUpdate.itemId === item.itemId &&
                  orderToUpdate.shipFromLocationId ===
                    item.shipFromLocationId &&
                  orderToUpdate.orderDate ===
                    dayjs(
                      opportunityDate.orderDate,
                      "YYYY-MM-DDTHH:mm:ss"
                    ).format("YYYY-MM-DD")
              );

              if (failedOrder && failedOrder.length > 0) {
                opportunityDate.newQuantity = failedOrder[0].quantityOrdered;
                opportunityDate.isFailedOrder = true;
                opportunityDate.failReason =
                  failedOrder[0].errors[0].message || "Error";
              }

              item.opportunityDates.push(opportunityDate);
            } else {
              item.opportunityDates.push({ orderDate: day, isDisabled: true });
            }
          });

          const orderHistoryDates = [];
          const orderHistoryQtys = [];
          let totalQty = 0;

          lastSixMondays.forEach((element) => {
            const week = (item.orderHistory ?? []).filter(
              (oh) => oh.weekCommenceFrom === element
            );

            if (week.length > 0) {
              orderHistoryDates.push(
                dayjs(week[0].weekCommenceFrom).format("DD/MM")
              );
              orderHistoryQtys.push(week[0].qty);
              totalQty += week[0].qty;
            } else {
              orderHistoryDates.push(dayjs(element).format("DD/MM"));
              orderHistoryQtys.push(0);
            }
          });
          item.orderHistoryHeaders = [...orderHistoryDates, "Total", "Avg"];
          /* Logic for Non Zero Avg */
          //   const nonZeroQtys = orderHistoryQtys.filter((qty) => qty !== 0);

          //   const avgQty =
          //     nonZeroQtys.length > 0 ? totalQty / nonZeroQtys.length : 0;

          //   item.orderHistoryValues = [...orderHistoryQtys, totalQty, avgQty];
          item.orderHistoryValues = [
            ...orderHistoryQtys,
            totalQty,
            Number((totalQty / orderHistoryQtys.length).toFixed(2)),
          ];
        });

        if (filteredData.length > 0) {
          if (offset < filteredData.length) {
            const data = [...filteredData];
            for (let i = offset; i < data.length; i++) {
              data[i] = orderOppurtunuties[i - offset];
            }
            setFilteredData(data);
          } else {
            setFilteredData((prevData) => [...prevData, ...orderOppurtunuties]);
          }
        } else {
          setFilteredData(orderOppurtunuties);
        }
        if (orderOppurtunuties.length <= 0) {
          setMessage(
            store && store.length > 0 ? NO_DATA_FOUND_MESSAGE : DEFAULT_MESSAGE
          );
        }
      } else {
        setFilteredData([]);
        setMessage(
          store && store.length > 0 ? NO_DATA_FOUND_MESSAGE : DEFAULT_MESSAGE
        );
      }
      setShowSpinner(false);
      setDataLoadingByScroll(false);
      dispatch(searchOrderOpportunitiesReset());
      if (updateDataOffset && updateDataOffset > 0) {
        const newOffset =
          updateDataOffset -
          orderOpportunitiesReducer.searchOrderOpportunitiesResponse.metadata
            .count;
        setUpdateDataOffset(newOffset);
      } else {
        setUpdateDataOffset(null);
        setFailedOrders([]);
      }
    } else if (orderOpportunitiesReducer.isSearchOrderOpportunitiesError) {
      setShowSpinner(false);
      setDataLoadingByScroll(false);
      if (
        orderOpportunitiesReducer.searchOrderOpportunitiesResponse.response
          .status === 404
      ) {
        if (filteredData.length <= 0) {
          setFilteredData([]);
          setMessage(
            store && store.length > 0 ? NO_DATA_FOUND_MESSAGE : DEFAULT_MESSAGE
          );
        } else {
          setMetadata({ ...metadata, next: "" });
        }
      } else {
        const { errTitle, errContent } = showErrorMessageByCode(
          orderOpportunitiesReducer.searchOrderOpportunitiesResponse
        );
        dispatch(showNotificationDialog(errTitle, errContent, "Ok"));
        dispatch(searchOrderOpportunitiesReset());
      }
    }
  }, [
    orderOpportunitiesReducer,
    dateFilter,
    dispatch,
    ordersToUpdateList,
    openDialogForCreate,
    store,
    totalOrdersCreated,
    totalCreateOrderFailed,
    totalUpdateOrderFailed,
    failedOrders,
    filteredData,
    offset,
    updateDataOffset,
    lastSixMondays,
    metadata,
  ]);

  useEffect(() => {
    if (processOrdersReducer.isProcessOrderLoading) {
      setShowSpinner(true);
    } else if (processOrdersReducer.isProcessOrderSuccess) {
      setShowSpinner(false);
      if (
        processOrdersReducer.processOrderResponse.orders &&
        processOrdersReducer.processOrderResponse.orders.length > 0
      ) {
        setFailedOrders([
          ...failedOrders,
          ...processOrdersReducer.processOrderResponse.orders,
        ]);
      }
      if (
        processOrdersReducer.processOrderResponse &&
        processOrdersReducer.processOrderResponse.metadata
      ) {
        const metadata = processOrdersReducer.processOrderResponse.metadata;
        const totalPassed = metadata.totalrecordsProcessed + totalOrdersUpdated;
        const totalFailed = totalUpdateOrderFailed + metadata.validationfailed;
        setTotalOrdersUpdated(totalPassed);
        setTotalUpdateOrderFailed(totalFailed);
        const totalCancelled = metadata.ordersDeleted + totalOrdersCancelled;
        const totalCancelledFailed =
          metadata.deleteValidationfailed + totalCancelOrderFailed;
        setTotalOrdersCancelled(totalCancelled);
        setTotalCancelOrderFailed(totalCancelledFailed);
        const totalBookMarked =
          totalOrdersBookmarked + metadata.bookmarkUpdated;
        setTotalOrdersBookmarked(totalBookMarked);
      }

      if (!openDialogForCreate) {
        setopenDialogForCreate(true);
      }
      dispatch({ type: RESET_PROCESS_ORDERS });
    } else if (processOrdersReducer.isProcessOrderError) {
      setShowSpinner(false);
      if (
        processOrdersReducer.processOrderResponse.orders &&
        processOrdersReducer.processOrderResponse.orders.length > 0
      ) {
        setFailedOrders([
          ...failedOrders,
          ...processOrdersReducer.processOrderResponse.orders,
        ]);
      }
      if (
        processOrdersReducer.processOrderResponse &&
        processOrdersReducer.processOrderResponse.metadata
      ) {
        const metadata = processOrdersReducer.processOrderResponse.metadata;
        const totalPassed = metadata.totalrecordsProcessed + totalOrdersUpdated;
        const totalFailed = totalUpdateOrderFailed + metadata.validationfailed;
        setTotalOrdersUpdated(totalPassed);
        setTotalUpdateOrderFailed(totalFailed);
        const totalCancelled = metadata.ordersDeleted + totalOrdersCancelled;
        const totalCancelledFailed =
          metadata.deleteValidationfailed + totalCancelOrderFailed;
        setTotalOrdersCancelled(totalCancelled);
        setTotalCancelOrderFailed(totalCancelledFailed);
        const totalBookMarked =
          totalOrdersBookmarked + metadata.bookmarkUpdated;
        setTotalOrdersBookmarked(totalBookMarked);
      }
      if (!openDialogForCreate) {
        setopenDialogForCreate(true);
      }
      dispatch({ type: RESET_PROCESS_ORDERS });
    }
  }, [
    processOrdersReducer,
    dispatch,
    openDialogForCreate,
    totalOrdersUpdated,
    totalUpdateOrderFailed,
    failedOrders,
    totalOrdersBookmarked,
    totalBookmarkFailed,
    totalBookmarksPosted,
    totalOrdersCancelled,
    totalCancelOrderFailed,
  ]);

  useEffect(() => {
    if (displayCutOffTime) {
      const [cutoffHour, cutoffMin] = displayCutOffTime.split(":").map(Number);
      const now = dayjs();
      const cutoffTime = dayjs()
        .set("hour", cutoffHour)
        .set("minute", cutoffMin)
        .set("second", 0)
        .set("millisecond", 0);

      const diffInMilliSeconds = cutoffTime.diff(now);

      if (diffInMilliSeconds > 0) {
        setCutOffReachedForToday(false);
        const timer = setTimeout(() => {
          setCutOffReachedForToday(true);
          dispatch(
            showNotificationDialog(
              "The cutoff time has been reached, no further orders can be entered",
              "To submit orders already entered please press the CREATE button within 15 minutes of this message appearing. Failure to press the create button within 15 minutes may result in items not being ordered.",
              "ok"
            )
          );
        }, diffInMilliSeconds);

        return () => clearTimeout(timer);
      } else {
        setCutOffReachedForToday(true);
      }
    }
  }, [displayCutOffTime, dispatch]);

  useEffect(() => {
    if (cutOffTimeResponse?.manualOrderConfigParams) {
      const displayCutOffEntry =
        cutOffTimeResponse.manualOrderConfigParams.find(
          (item) => item.action === CUTOFF_ACTION.display_cutOff_time
        );

      if (displayCutOffEntry) {
        setDisplayCutOffTime(displayCutOffEntry.cutoff);
      }
    }
  }, [cutOffTimeResponse]);

  const getOrderOpportunityDates = (weekDay, opportunityDates) => {
    let opportunity = null;
    if (opportunityDates) {
      opportunity = opportunityDates.filter(
        (item) =>
          dayjs(item.orderDate, "YYYY-MM-DDTHH:mm:ss").format(
            "YYYY-MM-DDT00:00:00"
          ) === weekDay
      );
    }
    return opportunity && opportunity.length > 0
      ? {
          ...opportunity[0],
          newQuantity: opportunity[0].quantityOrdered,
          isDisabled: dayjs().isAfter(opportunity[0].orderDate),
        }
      : null;
  };

  const handleSearch = () => {
    setOffset(0);
    setOrdersToUpdateList([]);
    setFilteredData([]);
    callSearchOrderOpportunities(0);
  };

  const callSearchOrderOpportunities = useCallback(
    (passedOffset) => {
      if (store && store.length > 0) {
        const mins = mpin ? mpin.split(",").map((num) => Number(num)) : null;

        if (mins !== null && mins.length > 50) {
          setMinError("MIN can have a maximum of 50 entries.");
        } else {
          const formattedFromDate = dayjs().format("YYYY-MM-DDT00:00:00");
          let formattedToDate =
            dateFilter === "Current Day"
              ? dayjs().format("YYYY-MM-DDT23:59:59")
              : dayjs().add(6, "day").format("YYYY-MM-DDT23:59:59");

          const payload = {
            itemIds: mins,
            shipToLocationIds: store,
            orderDateFrom: formattedFromDate,
            orderDateTo: formattedToDate,
            department: department && department.length > 0 ? department : null,
            category: category && category.length > 0 ? category : null,
            itemDescription: description.length > 0 ? description : null,
          };

          dispatch(
            searchOrderOpportunities(
              payload,
              FETCH_ORDER_LIMIT,
              passedOffset !== null && passedOffset !== undefined
                ? passedOffset
                : offset
            )
          );
        }
      } else {
        setLocationError("Please select location");
        setShowSpinner(false);
      }
    },
    [
      category,
      dateFilter,
      department,
      description,
      dispatch,
      mpin,
      offset,
      store,
    ]
  );

  useEffect(() => {
    if (metadata.next) {
      setDataLoadingByScroll(true);
      callSearchOrderOpportunities();
    }
  }, [offset]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateDataOffset != null) {
      callSearchOrderOpportunities(offset - updateDataOffset);
    }
  }, [updateDataOffset, callSearchOrderOpportunities, offset]);

  const handleCheckboxChange = (order) => {
    const checkStatus = !order.newBookmarkValue;

    setFilteredData((prevRows) => {
      const updatedRows = prevRows.map((row) =>
        row.itemId === order.itemId &&
        row.shipFromLocationId === order.shipFromLocationId
          ? { ...row, newBookmarkValue: checkStatus }
          : row
      );
      return updatedRows;
    });

    const ordersList = [...ordersToUpdateList];
    const orderToUpdateIndex = ordersList.findIndex(
      (item) =>
        item.itemId === order.itemId &&
        item.shipFromLocationId === order.shipFromLocationId &&
        item.isBookmark
    );
    if (orderToUpdateIndex > -1) {
      if (checkStatus === order.isBookmark) {
        ordersList.splice(orderToUpdateIndex, 1);
      } else {
        ordersList[orderToUpdateIndex].isBookmark = checkStatus;
      }
    } else {
      if (checkStatus !== order.isBookmark) {
        const newOrder = {
          itemId: order.itemId,
          packId: order.packId,
          itemDescription: order.itemDescription,
          packSize: order.packSize,
          shipFromLocationId: order.shipFromLocationId,
          shipToLocationId: order.shipToLocationId,
          isBookmark: checkStatus,
        };
        ordersList.push(newOrder);
      }
    }
    setOrdersToUpdateList(ordersList);
  };

  const handleLocationChange = (value) => {
    setStore(value ? [value] : []);
    setLocationError("");
  };

  const handleMinChange = (value) => {
    setMpin(value);
    setMinError("");
  };

  const handleCategoryChange = (event, value) => {
    setCategory(value);

    const filteredDepartments = storeCatalogueList
      .filter((order) => order.category === value)
      .map((order) => order.deparment);

    if (filteredDepartments && filteredDepartments.length > 1) {
      filteredDepartments.sort((a, b) => a.localeCompare(b));
    }
    setDepartmentList(filteredDepartments);
    setDepartment("");
  };

  const handleForCreateChange = async (event, value) => {
    const encryptedEmailId = await encryptString(userType.loginResponse?.email);
    const createOrderOpportunity = [];
    const updateOrderOppoutunity = [];
    const invalidOpportuities = [];
    ordersToUpdateList.forEach((order, index) => {
      if (order.isBookmark !== null && order.isBookmark !== undefined) {
        updateOrderOppoutunity.push(order);
      } else if (order.isValidQuantity) {
        if (order.manualOrderId) {
          updateOrderOppoutunity.push(order);
        } else {
          createOrderOpportunity.push(order);
        }
      } else {
        invalidOpportuities.push(order);
      }
    });

    setOrdersToUpdateList(invalidOpportuities);

    if (createOrderOpportunity.length > 0) {
      const createArrayForPayload = {
        orders: createOrderOpportunity,
        userId: encryptedEmailId,
        catalogueType: "storecatalogue",
      };
      dispatch(
        createManualOrdersForSiteCanteen(
          createArrayForPayload,
          "store-user",
          "create"
        )
      );
    }

    if (updateOrderOppoutunity.length > 0) {
      const updateArrayForPayload = {
        orders: updateOrderOppoutunity,
        userId: encryptedEmailId,
        catalogueType: "storecatalogue",
      };
      dispatch(
        amendOrdersForSiteCanteen(updateArrayForPayload, "store-user", "amend")
      );
    }
  };

  const handleCloseDialog = () => {
    sessionStorage.setItem("scrollPosition", window.scrollY);
    setFilteredData([]);
    setopenDialogForCreate(false);
    setTotalOrdersCreated(0);
    setTotalOrdersUpdated(0);
    setTotalBookmarksPosted(0);
    setTotalOrdersBookmarked(0);
    setTotalOrdersCancelled(0);
    setTotalCreateOrderFailed(0);
    setTotalUpdateOrderFailed(0);
    setTotalCancelOrderFailed(0);
    setTotalBookmarkFailed(0);
    setUpdateDataOffset(offset);
    // callSearchOrderOpportunities();
  };

  const isValidQuantityEntered = (newQty, order, dateIndex) => {
    if (order.opportunityDates[dateIndex].manualOrderId) {
      return (
        newQty !== null &&
        newQty !== undefined &&
        (order.maxQuantity ? newQty <= order.maxQuantity : true)
      );
    } else {
      return (
        newQty &&
        newQty > 0 &&
        (order.maxQuantity ? newQty <= order.maxQuantity : true)
      );
    }
  };

  const handleQuantityChange = (
    order,
    date,
    newQuantity,
    orderIndex,
    dateIndex
  ) => {
    const newQty = newQuantity === "" ? undefined : parseInt(newQuantity);
    const data = [...filteredData];
    const opportunityDatesToUpdate = data[orderIndex].opportunityDates;
    opportunityDatesToUpdate[dateIndex].newQuantity = newQty;
    opportunityDatesToUpdate[dateIndex].isFailedOrder = null;
    opportunityDatesToUpdate[dateIndex].failReason = null;

    const isValidQuantity = isValidQuantityEntered(newQty, order, dateIndex);

    setFilteredData((prevRows) => {
      const updatedRows = prevRows.map((row) =>
        row.itemId === order.itemId &&
        row.shipFromLocationId === order.shipFromLocationId
          ? { ...row, opportunityDates: opportunityDatesToUpdate }
          : row
      );
      return updatedRows;
    });

    const ordersList = [...ordersToUpdateList];
    const orderToUpdateIndex = ordersList.findIndex(
      (item) =>
        item.itemId === order.itemId &&
        item.shipFromLocationId === order.shipFromLocationId &&
        item.orderDate === date.orderDate
    );
    if (orderToUpdateIndex > -1) {
      if (
        data[orderIndex].opportunityDates[dateIndex].newQuantity ===
        data[orderIndex].opportunityDates[dateIndex].quantityOrdered
      ) {
        ordersList.splice(orderToUpdateIndex, 1);
      } else {
        ordersList[orderToUpdateIndex].quantityOrdered = newQty;
        ordersList[orderToUpdateIndex].isValidQuantity = isValidQuantity;
      }
    } else {
      if (
        data[orderIndex].opportunityDates[dateIndex].newQuantity !==
        data[orderIndex].opportunityDates[dateIndex].quantityOrdered
      ) {
        const newOrder = {
          manualOrderId: order.opportunityDates[dateIndex].manualOrderId,
          itemId: order.itemId,
          packId: order.packId,
          itemDescription: order.itemDescription,
          packSize: order.packSize,
          shipFromLocationId: order.shipFromLocationId,
          shipToLocationId: order.shipToLocationId,
          orderDate: order.opportunityDates[dateIndex].orderDate,
          deliveryDate: order.opportunityDates[dateIndex].deliveryDate,
          quantityOrdered: newQty,
          isValidQuantity: isValidQuantity,
        };
        ordersList.push(newOrder);
      }
    }

    setOrdersToUpdateList(ordersList);
  };

  const handlePrint = () => {
    const today = new Date();
    const currentDate = today.toLocaleDateString("en-GB").slice(0, 5); // Format dd/mm

    // Create new print window
    const printWindow = window.open("", "", "width=800,height=600");

    // Start building HTML content
    let htmlContent = `
    <html>
      <head>
        <title>Print Orders for ${currentDate}</title>
        <style>
          body { font-family: Arial, sans-serif; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid black; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; }
        </style>
      </head>
      <body>
        <h1>Orders for ${today.toLocaleDateString("en-GB")}</h1>
        <table>
          <thead>
            <tr>
              <th>MIN</th>
              <th>PIN</th>
              <th>Description & Order History</th>
              <th>Pack Size</th>
              <th>Max Qty</th>
              <th>Last Ordered Qty</th>
              <th>Last Ordered Date</th>
              <th>Quantity (${currentDate})</th>
            </tr>
          </thead>
          <tbody>
  `;

    // Filter valid rows
    const filteredDateForPrint = filteredData.filter(
      (item) => item.opportunityDates[0].quantityOrdered
    );

    // Build table rows
    filteredDateForPrint.forEach((order) => {
      const quantityForToday = order.opportunityDates[0].quantityOrdered || "";

      // Build order history sub-table
      let historyTable = `
        <table>
            <thead>
            <tr>
                ${order.orderHistoryHeaders
                  .map(
                    (header) =>
                      `<th style="border: 1px solid #ccc; padding: 4px 8px; background: #f5f5f5; text-align: center;">
                      ${header}
                    </th>`
                  )
                  .join("")}
            </tr>
            </thead>
            <tbody>
            <tr>
                ${order.orderHistoryValues
                  .map(
                    (value) =>
                      `<td style="border: 1px solid #ccc; padding: 4px 8px; text-align: center;">
                      ${value}
                    </td>`
                  )
                  .join("")}
            </tr>
            </tbody>
        </table>
        `;

      htmlContent += `
      <tr>
        <td>${order.itemId}</td>
        <td>${order.packId}</td>
        <td>${order.itemDescription}${historyTable}</td>
        <td>${order.packSize}</td>
        <td>${order.maxQuantity || ""}</td>
        <td>${order.lastOrderedActualQuantity || ""}</td>
        <td>${formatLastOrderDateForDisplay(order.lastOrderedDate)}</td>
        <td>${quantityForToday}</td>
      </tr>
    `;
    });

    // Close HTML
    htmlContent += `
          </tbody>
        </table>
      </body>
    </html>
  `;

    // Inject HTML into print window
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();

    // Focus and print
    printWindow.focus();
    printWindow.print();
  };

  const handleWeekDaysChange = (value) => {
    setDateFilter(value);
    setFilteredData([]);
    dispatch(searchOrderOpportunitiesReset());
  };

  const handlescroll = (e) => {
    // const { scrollTop, scrollLeft } = e.target;
    // setTableScrollPosition(scrollTop);
  };

  return (
    <div className="mainPageStyling commonPageStyling">
      {showSpinner && !isDataLoadingByScroll && (
        <Backdrop
          sx={{
            color: "#fff",
            flexDirection: "column",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={true}
        >
          <CircularProgress color="inherit" />

          <Typography variant="h6" sx={{ marginTop: 2 }}>
            Loading...
          </Typography>
        </Backdrop>
      )}
      <div style={{ display: "flex", alignItems: "center" }}>
        <Typography component="h1" variant="h5" sx={{ mb: 2, mr: 1 }}>
          Create Manual Order - Stores
        </Typography>
        <CutOffTimeAlert
          actionType={CUTOFF_ACTION.create_manual_order}
          operationType="store"
        />
      </div>

      <div style={{ marginBottom: "8px" }} className="labelControl">
        <Grid sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <Grid container spacing={3} sx={{ "& > *": { flex: 1 } }}>
            <Grid item xs={12} sm={6} md={1.7}>
              <LabelWithControl
                label={
                  <>
                    Location <span style={{ color: "red" }}>*</span>
                  </>
                }
                labelId="location"
              >
                <Autocomplete
                  className="dropDownValue locationDropDownValue"
                  id="location"
                  options={validStoreList}
                  defaultValue={validStoreList[0]}
                  renderInput={(params) => (
                    <TextField
                      helperText={locationError || ""}
                      error={locationError ? true : false}
                      {...params}
                      placeholder="Location"
                      variant="outlined"
                    />
                  )}
                  onChange={(event, value) => handleLocationChange(value)}
                  getOptionLabel={(option) => `${option}`}
                  style={{ width: "100%" }}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <LabelWithControl label="Department" labelId="category">
                <Autocomplete
                  className="dropDownValue"
                  id="category"
                  options={availableCategories}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Department"
                      variant="outlined"
                    />
                  )}
                  onChange={handleCategoryChange}
                  value={category}
                  style={{ width: "100%" }}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <LabelWithControl label="Category" labelId="department">
                <Autocomplete
                  className="dropDownValue"
                  id="department"
                  options={departmentList}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Category"
                      variant="outlined"
                    />
                  )}
                  onChange={(event, value) => setDepartment(value)}
                  style={{ width: "100%" }}
                  value={department}
                  disabled={!departmentList.length}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.9}>
              <LabelWithControl label="Min" labelId="min">
                <TextField
                  id="min"
                  variant="outlined"
                  placeholder="Min"
                  helperText={minError || ""}
                  error={minError ? true : false}
                  value={mpin}
                  onChange={(e) => handleMinChange(e.target.value)}
                  style={{ width: "100%" }}
                  fullWidth
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <LabelWithControl label="Description" labelId="description">
                <TextField
                  id="description"
                  value={description}
                  placeholder="Description"
                  onChange={(e) => setDescription(e.target.value)}
                  variant="outlined"
                  style={{ width: "100%" }}
                  fullWidth
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.4}>
              <LabelWithControl label="Week Days" labelId="weekdates">
                <Select
                  className="dropDownValue"
                  id="weekdates"
                  value={dateFilter}
                  onChange={(e) => handleWeekDaysChange(e.target.value)}
                  style={{ width: "100%" }}
                >
                  <MenuItem value="All Days">All Days</MenuItem>
                  <MenuItem value="Current Day">Current Day</MenuItem>
                </Select>
              </LabelWithControl>
            </Grid>

            <Grid item xs={12} sm={6} md={1}>
              <Button
                id="search"
                onClick={handleSearch}
                disabled={false}
                variant="contained"
                style={{ marginTop: "15px", height: "25px", fontSize: "12px" }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </div>

      {filteredData.length > 0 ? (
        <TableContainer
          id="ordersTable"
          component={Paper}
          className="tableContainerScroll AmendWrapper tableStructure tableStyle tableStyleStores"
          ref={tableContainerRef}
          onScroll={handlescroll}
          style={{ overflowX: "auto" }}
        >
          <Table>
            <TableHead>
              <StyledTableRow>
                <StyledTableCellSticky
                  stickyposition={0}
                  style={{
                    flex: 1,
                    minWidth: 30,
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                  }}
                >
                  {" "}
                </StyledTableCellSticky>
                <StyledTableCellSticky
                  stickyposition={30}
                  style={{ flex: 1, minWidth: 75 }}
                >
                  MIN
                </StyledTableCellSticky>
                <StyledTableCellSticky
                  stickyposition={105}
                  style={{ flex: 1, minWidth: 75 }}
                >
                  PIN
                </StyledTableCellSticky>
                <StyledTableCellSticky
                  stickyposition={180}
                  style={{ flex: 1, minWidth: 260 }}
                >
                  Description & Order History
                </StyledTableCellSticky>
                <StyledTableCellSticky
                  stickyposition={548}
                  style={{ flex: 1, minWidth: 50 }}
                >
                  Pack <br />
                  Size
                </StyledTableCellSticky>
                <StyledTableCellSticky
                  stickyposition={598}
                  style={{ flex: 1, minWidth: 60 }}
                >
                  Max Qty
                </StyledTableCellSticky>
                <StyledTableCellSticky
                  stickyposition={658}
                  style={{ flex: 1, minWidth: 70 }}
                >
                  Last <br />
                  Ordered Qty
                </StyledTableCellSticky>
                <StyledTableCellSticky
                  stickyposition={728}
                  style={{ flex: 1, minWidth: 75 }}
                >
                  Last <br />
                  Ordered Date
                </StyledTableCellSticky>
                {filteredData[0].opportunityDates.map((date) => (
                  <StyledTableCell
                    style={{ minWidth: 85, width: "85px" }}
                    className="headerCalender"
                    key={date.orderDate}
                  >
                    {formatDateForLabelDisplay(date.orderDate)}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((order, orderIndex) => (
                <StyledTableRow
                  key={`${order.itemId}_${order.shipFromLocationId}`}
                  sx={{
                    backgroundColor:
                      activeRowIndex === orderIndex
                        ? "rgba(0, 128, 255, 0.1)!important"
                        : "inherit",
                    transition: "background-color 0.3s ease",
                  }}
                >
                  <StyledTableCellSticky
                    stickyposition={0}
                    style={{
                      flex: 1,
                      minWidth: 30,
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#fff",
                      zIndex: 3, // Keep the sticky cell above other cells
                    }}
                    sx={{
                      backgroundColor:
                        activeRowIndex === orderIndex
                          ? "rgba(0, 128, 255, 0.1)!important"
                          : "inherit",
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    <Checkbox
                      checked={order.newBookmarkValue}
                      onChange={() => handleCheckboxChange(order)}
                      className="checkboxStyle"
                      tabIndex={-1}
                    />
                  </StyledTableCellSticky>
                  <StyledTableCellSticky
                    stickyposition={30}
                    style={{ flex: 1, minWidth: 75 }}
                    sx={{
                      backgroundColor:
                        activeRowIndex === orderIndex
                          ? "rgba(0, 128, 255, 0.1)!important"
                          : "inherit",
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    {order.itemId}
                  </StyledTableCellSticky>
                  <StyledTableCellSticky
                    stickyposition={105}
                    style={{ flex: 1, minWidth: 75 }}
                    sx={{
                      backgroundColor:
                        activeRowIndex === orderIndex
                          ? "rgba(0, 128, 255, 0.1)!important"
                          : "inherit",
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    {order.packId}
                  </StyledTableCellSticky>
                  <StyledTableCellSticky
                    stickyposition={180}
                    sx={{
                      backgroundColor:
                        activeRowIndex === orderIndex
                          ? "rgba(0, 128, 255, 0.1)!important"
                          : "inherit",
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    <Tooltip title={order.itemDescription} arrow>
                      <span
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "250px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {order.itemDescription}
                      </span>
                    </Tooltip>
                    {/* Add table below the tooltip */}
                    <table
                      style={{
                        marginTop: "5px",
                        borderCollapse: "collapse",
                        width: "100%",
                      }}
                    >
                      <thead>
                        <tr>
                          {order.orderHistoryHeaders.map((header) => (
                            <th
                              key={header}
                              style={{
                                border: "1px solid #ccc",
                                padding: "4px 8px",
                                backgroundColor: "#f5f5f5",
                                textAlign: "center",
                                color: "black",
                              }}
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {order.orderHistoryValues.map((value, idx) => (
                            <td
                              key={idx}
                              style={{
                                border: "1px solid #ccc",
                                padding: "4px 8px",
                                textAlign: "center",
                              }}
                            >
                              {value}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </StyledTableCellSticky>
                  <StyledTableCellSticky
                    stickyposition={548}
                    style={{ flex: 1, minWidth: 50 }}
                    sx={{
                      backgroundColor:
                        activeRowIndex === orderIndex
                          ? "rgba(0, 128, 255, 0.1)!important"
                          : "inherit",
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    {order.packSize}
                  </StyledTableCellSticky>
                  <StyledTableCellSticky
                    stickyposition={598}
                    style={{ flex: 1, minWidth: 60 }}
                    sx={{
                      backgroundColor:
                        activeRowIndex === orderIndex
                          ? "rgba(0, 128, 255, 0.1)!important"
                          : "inherit",
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    {order.maxQuantity}
                  </StyledTableCellSticky>
                  <StyledTableCellSticky
                    stickyposition={658}
                    style={{ flex: 1, minWidth: 70 }}
                    sx={{
                      backgroundColor:
                        activeRowIndex === orderIndex
                          ? "rgba(0, 128, 255, 0.1)!important"
                          : "inherit",
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    <span className="last-actual-quantity">
                      {order.lastOrderedActualQuantity}
                    </span>
                    {order.lastOrderedFulfilledQuantity <
                    order.lastOrderedActualQuantity ? (
                      <>
                        {" "}
                        /{" "}
                        <Tooltip
                          title={
                            order.orderStatus
                              ? order.orderStatus
                              : "Partially fulfilled"
                          }
                          arrow
                        >
                          <span className="last-fulfill-quantity">
                            {order.lastOrderedFulfilledQuantity}
                          </span>
                        </Tooltip>
                      </>
                    ) : null}
                  </StyledTableCellSticky>
                  <StyledTableCellSticky
                    stickyposition={728}
                    style={{ flex: 1, minWidth: 75 }}
                    sx={{
                      backgroundColor:
                        activeRowIndex === orderIndex
                          ? "rgba(0, 128, 255, 0.1)!important"
                          : "inherit",
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    {formatLastOrderDateForDisplay(order.lastOrderedDate)}
                  </StyledTableCellSticky>
                  {order.opportunityDates.map((date, index) => {
                    const quantity =
                      date.newQuantity !== null &&
                      date.newQuantity !== undefined
                        ? date.newQuantity
                        : "";

                    function isSameDate(d1, d2) {
                      return (
                        d1.getFullYear() === d2.getFullYear() &&
                        d1.getMonth() === d2.getMonth() &&
                        d1.getDate() === d2.getDate()
                      );
                    }
                    const matchingOrder = filteredData?.find(
                      (item) => item.itemId === order.itemId
                    );

                    const matchingDateObj = matchingOrder?.dates?.find(
                      (d) =>
                        new Date(d.orderDate).toISOString() ===
                        new Date(date.orderDate).toISOString()
                    );

                    const isToday =
                      matchingDateObj &&
                      isSameDate(
                        new Date(matchingDateObj.orderDate),
                        new Date()
                      );

                    // Disable if date is disabled OR (if it's today and cutoff passed)

                    const isDisabled =
                      date.isDisabled || (isToday && isCutOffReachedForToday);
                    const isExceedingMax =
                      !isNaN(quantity) &&
                      parseInt(quantity, 10) > order.maxQuantity;
                    const isLessThanMin =
                      !date.manualOrderId &&
                      !isNaN(quantity) &&
                      parseInt(quantity, 10) <= 0;
                    const isValueChanged =
                      date.newQuantity &&
                      date.newQuantity !== date.quantityOrdered;
                    return (
                      <StyledTableCell
                        key={date.orderDate}
                        style={{ flex: 1, minWidth: 85, width: "85px" }}
                        sx={{
                          backgroundColor:
                            activeRowIndex === orderIndex
                              ? "rgba(0, 128, 255, 0.1)!important"
                              : "inherit",
                          transition: "background-color 0.3s ease",
                        }}
                      >
                        <TextField
                          className="inputBox"
                          id={`${order.itemId}_${date.orderDate}`}
                          value={quantity}
                          placeholder={isDisabled ? "X" : "Qty"}
                          variant="outlined"
                          size="small"
                          inputProps={{ maxLength: 3 }}
                          style={{ width: "54px", textAlign: "center" }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                border: date.isFailedOrder
                                  ? "2px solid #ed1909"
                                  : isLessThanMin || isExceedingMax
                                  ? "2px solid #F28C28"
                                  : isValueChanged
                                  ? "2px solid #004e37"
                                  : "1px solid none", // Change border color based on value
                              },
                              "&.Mui-focused fieldset": {
                                border: date.isFailedOrder
                                  ? "2px solid #ed1909"
                                  : isLessThanMin || isExceedingMax
                                  ? "2px solid #F28C28"
                                  : isValueChanged
                                  ? "2px solid #004e37"
                                  : "1px solid none", // Change border color when focused
                              },
                              "&:hover fieldset": {
                                border: date.isFailedOrder
                                  ? "2px solid #ed1909"
                                  : isLessThanMin || isExceedingMax
                                  ? "2px solid #F28C28"
                                  : isValueChanged
                                  ? "2px solid #004e37"
                                  : "1px solid none", // Change border color on hover
                              },
                            },
                          }}
                          onChange={(e) => {
                            handleQuantityChange(
                              order,
                              date,
                              e.target.value.replace(/[^0-9, ]/g, ""),
                              orderIndex,
                              index
                            );
                            setActiveRowIndex(orderIndex); // highlight this row
                          }}
                          onBlur={() => setActiveRowIndex(null)} // remove highlight when done
                          disabled={isDisabled}
                          InputProps={{
                            style: {
                              background: isDisabled ? "#ccc" : "inherit",
                              textAlign: "center",
                            },
                          }}
                        />
                        {date.isFailedOrder ? (
                          <>
                            <Tooltip title={date.failReason} arrow>
                              <InfoOutlinedIcon
                                sx={{
                                  color: "#ed1909",
                                  marginTop: "0px",
                                  fontSize: "18px",
                                  marginLeft: "4px",
                                }}
                              />
                            </Tooltip>
                          </>
                        ) : (
                          !isDisabled &&
                          (isLessThanMin || isExceedingMax) && (
                            <Tooltip
                              title={
                                isLessThanMin
                                  ? "Order quantity must be greater than zero"
                                  : `Order quantity must be less than max order quantity`
                              }
                              arrow
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  color: "#F28C28",
                                  marginTop: "0px",
                                  fontSize: "18px",
                                  marginLeft: "4px",
                                }}
                              />
                            </Tooltip>
                          )
                        )}
                      </StyledTableCell>
                    );
                  })}
                </StyledTableRow>
              ))}
              {isDataLoadingByScroll && (
                <StyledTableRow>
                  <StyledTableCell
                    colSpan={14}
                    style={{
                      textAlign: "center",
                      position: "relative",
                      height: "100px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 12,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress />
                    </div>
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer className="tableStructure tableStyle">
          <Table>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={16}
                  style={{ textAlign: "center", borderRight: "0" }}
                >
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      color: "black",
                      marginBottom: "10px",
                    }}
                  >
                    {message}
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialogForCreate} onClose={handleCloseDialog}>
        <DialogTitle>
          Manual Orders submitted successfully, refer below for the exact
          status:{" "}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div className="totalOrdersCreated">
              Total number of manual orders ={" "}
              {totalOrdersCreated +
                totalCreateOrderFailed +
                totalOrdersUpdated +
                totalUpdateOrderFailed +
                totalOrdersCancelled +
                totalCancelOrderFailed}
            </div>
            <div className="totalOrdersCreated">
              <span>Orders created sucessfully = {totalOrdersCreated}</span>
              &nbsp;&nbsp;&nbsp;<span>failed = {totalCreateOrderFailed}</span>
            </div>
            <div className="totalOrdersCreated">
              <span>Orders amended sucessfully = {totalOrdersUpdated}</span>
              &nbsp;&nbsp;&nbsp;<span>failed = {totalUpdateOrderFailed}</span>
            </div>
            <div className="totalOrdersCreated">
              <span>Orders cancelled sucessfully = {totalOrdersCancelled}</span>
              &nbsp;&nbsp;&nbsp;<span>failed = {totalCancelOrderFailed}</span>
            </div>
            <div className="totalOrdersCreated">
              <span>Items bookmarked = {totalOrdersBookmarked}</span>
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <div className="commonButtons">
        <Grid
          container
          spacing={2}
          sx={{ marginTop: "5px", marginBottom: "5px" }}
        >
          <Grid item xs={4} sm={4} md={10} className="buttonPadding">
            <Button
              variant="contained"
              color="primary"
              onClick={handlePrint}
              disabled={filteredData && filteredData.length <= 0}
            >
              Print
            </Button>
          </Grid>
          <Grid
            item
            xs={4}
            sm={4}
            md={1}
            className="buttonPadding"
            sx={{ textAlign: "right" }}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={!enableCreateButton}
              onClick={handleForCreateChange}
            >
              Create
            </Button>
          </Grid>
          <Grid
            item
            xs={4}
            sm={4}
            md={1}
            className="buttonPadding"
            sx={{ textAlign: "right" }}
          >
            <Button
              variant="contained"
              color="primary"
              sx={{
                "&:hover": { backgroundColor: "#D3D3D3" },
                color: "black",
                backgroundColor: "#D3D3D3",
                textTransform: "none",
              }}
              onClick={() => navigate("/home")}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};
